import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react'
import { api } from '../../lib/api'
import { Table, type Colonna } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { formatDataOra } from '../../lib/format'
import pagina from '../pagine.module.css'

interface RigaAudit {
  id: string
  user_id: string | null
  azione: string
  target_id: string | null
  target_type: string | null
  dettagli: Record<string, unknown> | null
  created_at: string
}

interface Risposta {
  righe: RigaAudit[]
  totale: number
  page: number
  per_pagina: number
}

export function AdminAuditLog() {
  const [page, setPage] = useState(1)
  const [azione, setAzione] = useState('')

  const q = useQuery({
    queryKey: ['admin', 'audit', page, azione],
    queryFn: async () => {
      const params: Record<string, unknown> = { page, per_pagina: 50 }
      if (azione) params.azione = azione
      return (await api.get<{ dati: Risposta }>('/superadmin/audit-log', { params })).data.dati
    },
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento" />

  const colonne: Colonna<RigaAudit>[] = [
    { chiave: 'data', intestazione: 'Data', render: (r) => formatDataOra(r.created_at), larghezza: '170px' },
    {
      chiave: 'azione', intestazione: 'Azione',
      render: (r) => <Badge variante={varianteAzione(r.azione)}>{r.azione}</Badge>,
      larghezza: '240px',
    },
    {
      chiave: 'target', intestazione: 'Target',
      render: (r) => (
        <div>
          <div style={{ fontSize: 13 }}>{r.target_type ?? '—'}</div>
          {r.target_id && (
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {r.target_id.slice(0, 8)}…
            </div>
          )}
        </div>
      ),
      larghezza: '180px',
    },
    {
      chiave: 'utente', intestazione: 'Eseguito da',
      render: (r) => r.user_id ? (
        <span className="mono" style={{ fontSize: 12 }}>{r.user_id.slice(0, 8)}…</span>
      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>,
      larghezza: '140px',
    },
    {
      chiave: 'dettagli', intestazione: 'Dettagli',
      render: (r) => r.dettagli ? (
        <code style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {JSON.stringify(r.dettagli).slice(0, 60)}…
        </code>
      ) : '—',
    },
  ]

  const totalePagine = Math.max(1, Math.ceil(q.data.totale / q.data.per_pagina))

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Audit log</h1>
          <div className={pagina.sottotitolo}>{q.data.totale} eventi registrati</div>
        </div>
      </div>

      <div className={pagina.filtri}>
        <select className={pagina.select} value={azione} onChange={(e) => { setAzione(e.target.value); setPage(1) }}>
          <option value="">Tutte le azioni</option>
          <option value="piano_aggiornato_stripe">Piano aggiornato</option>
          <option value="subscription_cancellata">Subscription cancellata</option>
          <option value="utente_aggiornato_superadmin">Utente modificato</option>
          <option value="team_aggiornato_superadmin">Team modificato</option>
          <option value="impersonazione">Impersonazione</option>
          <option value="membro_attivato">Membro attivato</option>
          <option value="membro_disattivato">Membro disattivato</option>
          <option value="membro_rimosso">Membro rimosso</option>
        </select>
      </div>

      <Table
        colonne={colonne}
        righe={q.data.righe}
        vuoto={<EmptyState icona={<ClipboardList size={40} />} titolo="Nessun evento" />}
      />

      {totalePagine > 1 && (
        <div className={pagina.paginazione}>
          <span>Pagina {q.data.page} di {totalePagine}</span>
          <div className={pagina.paginazioneBottoni}>
            <Button taglia="sm" variante="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)} iconaSinistra={<ChevronLeft size={14} />}>
              Indietro
            </Button>
            <Button taglia="sm" variante="secondary" disabled={page >= totalePagine} onClick={() => setPage(page + 1)} iconaDestra={<ChevronRight size={14} />}>
              Avanti
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function varianteAzione(a: string): 'default' | 'info' | 'warning' | 'danger' | 'accent' {
  if (a.includes('impersonazione')) return 'accent'
  if (a.includes('cancellata') || a.includes('rimosso') || a.includes('disattivato')) return 'danger'
  if (a.includes('stripe')) return 'info'
  if (a.includes('aggiornato')) return 'warning'
  return 'default'
}
